import { MessageTypes } from 'components/billing/common/types';
import { useState } from 'react';
import { saveImageFile } from '../utils/messages';

const IMAGE_MIME_TYPE_PREFIX = 'image/';
const VIDEO_MIME_TYPE_PREFIX = 'video/';

// Note: Keeping the number of bytes in decimal for consistency with OS storage practices
export const DEFAULT_MAX_IMAGE_FILE_SIZE = 1000000; // 1 MB
export const RECOMMENDED_IMAGE_BYTES_THRESHOLD = 500000; // 500 KB
export const DEFAULT_MAX_IMAGE_FILE_SIZE_ERROR =
  'File size must be less than 1 MB (we recommend less than 500 KB for optimal sizing & resolution)';
export const compressedMessage = `Images may be compressed to fit file size limit.`;
export const carrierSupportMessage = `Images and GIFs (MMS) aren't supported by all carriers or countries. Plain text (SMS) without media may be sent instead.`;

// https://help.twilio.com/articles/360018832773-Twilio-Programmable-SMS-Supported-File-Types-and-Size-Limits-for-MMS-Media-Messages#h_01HBGHJ4R9BKZCF9QVYTCSARSM
export const RESIZED_MMS_IMAGE_SIZE = {
  label: '600 KB',
  value: 600000,
};
export const RESIZED_RCS_IMAGE_CARD = {
  label: '2 MB',
  value: 2000000,
};
export const RESIZED_RCS_IMAGE_CAROUSEL = {
  label: '1 MB',
  value: 1000000,
};

interface Options {
  messageType?: MessageTypes;
  maxImageSize?: number;
  maxImageSizeError?: string;
  imageUploadUrl?: string;
  skipSizeValidation?: boolean;
}

const validateImageFile = (file: File, options?: Options) => {
  const isMMS = options?.messageType === 'MMS';
  const isRCS = options?.messageType?.startsWith('RCS');

  if (isMMS && !file.type.startsWith(IMAGE_MIME_TYPE_PREFIX)) {
    throw new Error('File type is not allowed');
  }

  if (
    isRCS &&
    !file.type.startsWith(IMAGE_MIME_TYPE_PREFIX) &&
    !file.type.startsWith(VIDEO_MIME_TYPE_PREFIX)
  ) {
    throw new Error('File type is not allowed');
  }

  if (
    !options?.skipSizeValidation &&
    file.size > (options?.maxImageSize || DEFAULT_MAX_IMAGE_FILE_SIZE)
  ) {
    throw new Error(
      options?.maxImageSizeError || DEFAULT_MAX_IMAGE_FILE_SIZE_ERROR,
    );
  }
};

export type ImageFileUpload = [
  isUploadingImageFile: boolean,
  uploadImageFile: (input: File | string) => Promise<string>,
  validateImageFile: (file: File) => void,
];

const useImageFileUpload = (options?: Options): ImageFileUpload => {
  const [isUploadingImageFile, setUploadingImageFile] = useState(false);

  const uploadImageFile = async (input: File | string) => {
    try {
      if (input instanceof File) {
        validateImageFile(input, options);
      }
      setUploadingImageFile(true);

      const mediaUrl = await saveImageFile(input, options?.imageUploadUrl);
      setUploadingImageFile(false);
      return mediaUrl;
    } catch (err: unknown) {
      setUploadingImageFile(false);
      throw err;
    }
  };

  return [isUploadingImageFile, uploadImageFile, validateImageFile];
};

export default useImageFileUpload;
